import QRCode from 'qrcode';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { isActive, LATE_MINUTES, laneOf, minutesSince } from '../lib/kitchen';
import { usePollWhileOffline } from '../lib/live';
import { navigate } from '../lib/router';
import { baht } from '../lib/text';
import type { Order, Takeout } from '../lib/types';
import { Checkout } from './Checkout';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

interface Table { id: string; name: string; seats: number; session: { id: string; guests: number; openedAt: string; billRequestedAt?: string } | null }

/** /floor: tables as tiles + open takeouts; seat, add dishes, check out. Live over WebSocket. */
export function Floor({ path }: { path: string }) {
  const [tables, setTables] = useState<Table[]>([]);
  const [takeout, setTakeout] = useState<Takeout[]>([]);
  const [active, setActive] = useState<Order[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<{ id: string; title: string } | null>(null);
  const [now, setNow] = useState(Date.now());
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (token: string) => {
    try {
      const [t, k, to] = await Promise.all([
        api<{ tables: Table[] }>('GET', '/tables', { token }),
        api<{ orders: Order[] }>('GET', '/kitchen', { token }),
        api<{ takeout: Takeout[] }>('GET', '/takeout', { token }),
      ]);
      setTables(t.tables);
      setActive(k.orders);
      setTakeout(to.takeout);
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  }, []);

  const { s, online } = useStaff((e) => {
    if (e.kind === 'table' || e.kind === 'session') { if (s) void load(s.token); }
    if (e.kind === 'order') {
      const o = e.order as Order;
      setActive((prev) => [...prev.filter((x) => x.id !== o.id), ...(isActive(o) ? [o] : [])]);
    }
  }, () => { if (s) void load(s.token); });
  useEffect(() => { if (s) void load(s.token); }, [s, load]);
  usePollWhileOffline(online, () => { if (s) void load(s.token); });
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);

  const bySession = useMemo(() => {
    const m = new Map<string, Order[]>();
    for (const o of active) m.set(o.sessionId, [...(m.get(o.sessionId) ?? []), o]);
    return m;
  }, [active]);

  const stateOf = (sessionId: string | undefined, billRequested?: string) => {
    if (!sessionId) return { cls: 'free', label: 'ว่าง · Free' };
    const orders = bySession.get(sessionId) ?? [];
    const cooking = orders.filter((o) => laneOf(o) === 'new' || laneOf(o) === 'cooking');
    if (cooking.some((o) => minutesSince(o.placedAt, now) >= LATE_MINUTES)) return { cls: 'late', label: 'ช้า · Late' };
    if (billRequested) return { cls: 'bill', label: 'เช็คบิล · Bill' };
    if (cooking.length) return { cls: 'cooking', label: 'รออาหาร · Cooking' };
    if (orders.length) return { cls: 'eating', label: 'รอเสิร์ฟ · To serve' };
    return { cls: 'seated', label: 'นั่งแล้ว · Seated' };
  };

  const newTakeout = async () => {
    if (!s) return;
    const name = prompt('ชื่อลูกค้า (ไม่บังคับ) · Customer name (optional)') ?? '';
    try {
      const t = await api<Takeout>('POST', '/takeout', { token: s.token, body: name.trim() ? { name: name.trim() } : {} });
      navigate(`/order/${t.id}`);
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  };
  const accept = async (t: Takeout) => {
    if (!s) return;
    try { await api('POST', `/takeout/${t.id}/accept`, { token: s.token }); void load(s.token); } catch (e) { setErr((e as Error).message); }
  };

  const seated = tables.filter((t) => t.session).length;
  const selected = tables.find((t) => t.id === sel) ?? null;
  return (
    <Shell path={path} online={online} sub={`${seated}/${tables.length} tables · ${takeout.length} takeout`}>
      {err && <div className="warn" role="alert" style={{ color: 'var(--ink)', marginBottom: 12 }}>{err}</div>}

      <section className="panel" aria-label="Takeout" style={{ marginBottom: 14 }}>
        <div className="row" style={{ marginBottom: takeout.length ? 10 : 0 }}>
          <h2 className="display" style={{ margin: 0, fontSize: 18 }}>กลับบ้าน · Takeout</h2>
          <button className="btn-mango" onClick={() => void newTakeout()}>+ สั่งกลับบ้าน · New takeout</button>
        </div>
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto' }}>
          {takeout.map((t) => {
            const st = !t.accepted ? { cls: 'pending', label: 'รอยืนยัน · New online order' } : stateOf(t.id, t.billRequestedAt);
            return (
              <div key={t.id} className={`tile ${st.cls}`} style={{ minWidth: 200, cursor: 'default' }}>
                <div className="row" style={{ width: '100%' }}>
                  <span className="display" style={{ fontSize: 20 }}>{t.pickupNo}</span>
                  <span className="tchip">{st.label}</span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{[t.customerName, t.phone].filter(Boolean).join(' · ') || (t.source === 'online' ? 'Online' : 'Counter')} · {minutesSince(t.openedAt, now)} min</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {!t.accepted && <button className="dish-step queued" onClick={() => void accept(t)}>รับออร์เดอร์ · Accept</button>}
                  <button className="dish-step" onClick={() => navigate(`/order/${t.id}`)}>+ เมนู</button>
                  <button className="dish-step ready" onClick={() => setCheckout({ id: t.id, title: t.pickupNo })}>เช็คบิล</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="layout">
        <section className="floorplan main" aria-label="Floor plan">
          <div className="tiles">
            {tables.map((t) => {
              const st = stateOf(t.session?.id, t.session?.billRequestedAt);
              return (
                <button key={t.id} className={`tile ${st.cls}`} aria-pressed={sel === t.id} onClick={() => setSel(t.id)} aria-label={`Table ${t.name}, ${st.label}`}>
                  <div className="row" style={{ width: '100%' }}>
                    <span className="display" style={{ fontSize: 20 }}>{t.name}</span>
                    <span className="tchip" style={st.cls === 'late' ? { background: 'var(--red)' } : st.cls === 'bill' ? { background: '#6A44D8' } : undefined}>{st.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {t.session ? `${t.session.guests || '–'} guests · ${minutesSince(t.session.openedAt, now)} min` : `${t.seats} seats`}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
        <aside className="aside">
          {selected ? (
            <TablePanel key={selected.id + (selected.session?.id ?? '')} t={selected} token={s?.token ?? ''}
              onChange={() => s && void load(s.token)} onCheckout={(id) => setCheckout({ id, title: `โต๊ะ ${selected.name}` })} />
          ) : <div className="panel"><p style={{ margin: 0, color: '#A9AFC6' }}>แตะโต๊ะเพื่อเปิด สั่งอาหาร หรือเช็คบิล · Tap a table to seat, order or check out.</p></div>}
        </aside>
      </div>

      {checkout && s && (
        <Checkout token={s.token} sessionId={checkout.id} title={checkout.title} onClose={() => setCheckout(null)}
          onDone={() => { setCheckout(null); setSel(null); void load(s.token); }} />
      )}
    </Shell>
  );
}

function TablePanel({ t, token, onChange, onCheckout }: { t: Table; token: string; onChange: () => void; onCheckout: (sessionId: string) => void }) {
  const [guests, setGuests] = useState(2);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [qr, setQr] = useState('');
  const [bill, setBill] = useState<{ totalSatang: number; lines: unknown[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!t.session) return;
    void api<{ joinUrl: string }>('GET', `/sessions/${t.session.id}/join`, { token }).then((r) => setJoinUrl(r.joinUrl), () => undefined);
    void api<{ totalSatang: number; lines: unknown[] }>('GET', `/sessions/${t.session.id}/bill`, { token }).then(setBill, () => undefined);
  }, [t.session, token]);
  useEffect(() => {
    if (joinUrl) void QRCode.toString(joinUrl, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' }).then(setQr);
  }, [joinUrl]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try { await fn(); onChange(); } catch (e) { handleAuthError(e); setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <section className="panel side" aria-label={`Table ${t.name}`}>
      <h2 className="display" style={{ margin: 0, fontSize: 22 }}>โต๊ะ {t.name}</h2>
      {!t.session ? (
        <>
          <label className="field" style={{ color: '#F2F0E9' }}>จำนวนลูกค้า · Guests
            <div className="stepper" style={{ alignSelf: 'flex-start', color: 'var(--ink)' }}>
              <button onClick={() => setGuests(Math.max(1, guests - 1))} aria-label="Fewer guests">−</button>
              <span>{guests}</span>
              <button onClick={() => setGuests(Math.min(100, guests + 1))} aria-label="More guests">+</button>
            </div>
          </label>
          <button className="btn-mango" disabled={busy} onClick={() => void act(async () => {
            const r = await api<{ joinUrl: string }>('POST', `/tables/${t.id}/sessions`, { token, body: { guests } });
            setJoinUrl(r.joinUrl);
          })}>เปิดโต๊ะ · Seat guests</button>
        </>
      ) : (
        <>
          {t.session.billRequestedAt && <div className="warn" style={{ color: 'var(--ink)' }}>ลูกค้าขอเช็คบิล · Guests asked for the bill</div>}
          {qr ? (
            <>
              <div className="qr" role="img" aria-label={`QR code to order at table ${t.name}`} dangerouslySetInnerHTML={{ __html: qr }} />
              <span style={{ fontSize: 13, color: '#A9AFC6', textAlign: 'center' }}>ให้ลูกค้าสแกนเพื่อสั่งอาหาร · Guests scan to order</span>
            </>
          ) : <div className="spinner" role="status" aria-label="Loading QR" style={{ alignSelf: 'center' }} />}
          {bill && <div className="row" style={{ fontWeight: 700 }}><span>{bill.lines.length} dishes</span><span className="display" style={{ color: 'var(--mango)' }}>{baht(bill.totalSatang)}</span></div>}
          <button className="btn-dark" onClick={() => navigate(`/order/${t.session!.id}`)}>+ สั่งอาหาร · Add dishes</button>
          <button className="btn-mango" onClick={() => onCheckout(t.session!.id)}>เช็คบิล · Checkout</button>
          {bill && bill.lines.length === 0 && (
            <button className="btn-dark" disabled={busy} onClick={() => void act(() => api('POST', `/sessions/${t.session!.id}/close`, { token }))}>ปิดโต๊ะ (ไม่มีรายการ) · Close (nothing ordered)</button>
          )}
        </>
      )}
      {err && <div className="warn" role="alert" style={{ color: 'var(--ink)' }}>{err}</div>}
    </section>
  );
}
